// AI triage of Help submissions (FeedbackItems.AITriage*, db/06; system
// prompt category 'feedback_triage', db/05). Pure helpers only — the network
// calls (prompt fetch → Claude proxy → sp_SaveFeedbackTriage) live in
// Help.jsx's triageFeedbackItem so these stay unit-testable.
//
// ADVISORY BY DESIGN. Triage classifies and proposes; it never implements,
// merges or deploys. `auto_candidate` means "bounded enough to hand to a
// coding agent as a PR for human review" — the human still reviews and
// merges (see docs/implementation/FEEDBACK_AI_TRIAGE.md).

export const COMPLEXITY_LEVELS = [
  { key: "trivial", label: "Trivial", bg: "#D1FAE5", color: "#065F46", hint: "label / option / default in one place" },
  { key: "small",   label: "Small",   bg: "#DBEAFE", color: "#1E40AF", hint: "one component or one proc, additive only" },
  { key: "medium",  label: "Medium",  bg: "#FEF3C7", color: "#92400E", hint: "several files or a bounded calculation / workflow change" },
  { key: "large",   label: "Large",   bg: "#FEE2E2", color: "#991B1B", hint: "new module, migration, auth / pricing / turnover" },
  { key: "unclear", label: "Unclear", bg: "#F3F4F6", color: "#374151", hint: "cannot be scoped from the submission" },
];
export const COMPLEXITY_BY_KEY = Object.fromEntries(COMPLEXITY_LEVELS.map(l => [l.key, l]));
export const TRIAGE_CATEGORIES = ["bug", "enhancement", "new_feature", "data", "question", "other"];

const str = (v, max = 2000) => (v == null ? "" : String(v)).trim().slice(0, max);
const strList = (v, max = 10) => (Array.isArray(v) ? v : v ? [v] : []).map(x => str(x, 400)).filter(Boolean).slice(0, max);

// Content blocks for the Claude call: the submission as text, then each
// screenshot as an image block (base64 pulled out of its data URL). A data
// URL that is not an image is skipped rather than failing the whole call.
export function buildTriageUserContent({ title, description, type, priority, submittedBy, attachments = [] }) {
  const lines = [
    `TYPE: ${str(type, 40) || "Unknown"}`,
    `PRIORITY (as submitted): ${str(priority, 20) || "Medium"}`,
    `TITLE: ${str(title, 200)}`,
    "",
    "DESCRIPTION:",
    str(description, 4000) || "(none)",
  ];
  if (submittedBy) lines.push("", `SUBMITTED BY: ${str(submittedBy, 200)}`);
  const images = [];
  for (const a of attachments) {
    const m = String(a?.dataUrl || a?.DataUrl || "").match(/^data:(image\/[a-z0-9.+-]+);base64,([A-Za-z0-9+/=]+)$/i);
    if (!m) continue;
    images.push({ type: "image", source: { type: "base64", media_type: m[1].toLowerCase(), data: m[2] } });
  }
  if (images.length) lines.push("", `${images.length} screenshot${images.length === 1 ? "" : "s"} attached below.`);
  return [{ type: "text", text: lines.join("\n") }, ...images];
}

// The model is told to answer with one JSON object and nothing else, but be
// tolerant: strip ``` fences, take the first {...} span, and normalize every
// field to a known shape so the inbox never renders undefined. Throws only
// when there is no parseable object at all.
export function parseTriageResponse(text) {
  const raw = String(text || "").trim().replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "");
  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start < 0 || end <= start) throw new Error("Triage response contained no JSON object");
  let obj;
  try { obj = JSON.parse(raw.slice(start, end + 1)); }
  catch (e) { throw new Error(`Triage response was not valid JSON: ${e.message}`); }
  return normalizeTriage(obj);
}

export function normalizeTriage(obj = {}) {
  const complexity = COMPLEXITY_BY_KEY[str(obj.complexity, 20).toLowerCase()] ? str(obj.complexity, 20).toLowerCase() : "unclear";
  const category = TRIAGE_CATEGORIES.includes(str(obj.category, 30).toLowerCase()) ? str(obj.category, 30).toLowerCase() : "other";
  let confidence = Number(obj.confidence);
  if (!Number.isFinite(confidence)) confidence = 0;
  confidence = Math.min(1, Math.max(0, confidence));
  const questions = strList(obj.questions);
  // auto_candidate is re-derived here, never trusted from the model: the
  // rule is the prompt's rule, enforced in code.
  const autoCandidate = (complexity === "trivial" || complexity === "small") && confidence >= 0.7 && questions.length === 0;
  return {
    category,
    area: str(obj.area, 100),
    complexity,
    confidence,
    summary: str(obj.summary, 600),
    proposedChange: str(obj.proposed_change ?? obj.proposedChange, 2000),
    likelyFiles: strList(obj.likely_files ?? obj.likelyFiles, 12),
    questions,
    riskNotes: str(obj.risk_notes ?? obj.riskNotes, 1000),
    duplicateHint: str(obj.duplicate_hint ?? obj.duplicateHint, 300),
    autoCandidate,
  };
}

// FeedbackItems.AITriageJson → triage object (null when absent / unreadable).
export function parseStoredTriage(item) {
  const raw = item?.AITriageJson;
  if (!raw) return null;
  try { return normalizeTriage(typeof raw === "string" ? JSON.parse(raw) : raw); }
  catch { return null; }
}

// sp_SaveFeedbackTriage payload.
export function triageSavePayload(feedbackId, triage, model) {
  return {
    feedback_id: Number(feedbackId),
    complexity: triage.complexity,
    area: triage.area || null,
    model: model || null,
    triage_json: triage,
  };
}

// Inbox filter: which items match a complexity chip ('all' | a level key |
// 'untriaged' | 'auto').
export function matchesComplexityFilter(item, filter) {
  if (!filter || filter === "all") return true;
  const t = parseStoredTriage(item);
  if (filter === "untriaged") return !t;
  if (filter === "auto") return !!t?.autoCandidate;
  return (t?.complexity || item?.AIComplexity || null) === filter;
}

export function complexityCounts(items = []) {
  const counts = { all: items.length, untriaged: 0, auto: 0 };
  for (const l of COMPLEXITY_LEVELS) counts[l.key] = 0;
  for (const it of items) {
    const t = parseStoredTriage(it);
    if (!t) { counts.untriaged += 1; continue; }
    counts[t.complexity] = (counts[t.complexity] || 0) + 1;
    if (t.autoCandidate) counts.auto += 1;
  }
  return counts;
}

// A ready-to-paste brief for a developer (or a coding agent run BY a
// developer) — the hand-off artifact for an auto_candidate. Markdown. Says
// explicitly that the result is a PR for human review, never a merge.
export function implementationBrief(item, triage) {
  const t = triage || parseStoredTriage(item) || normalizeTriage({});
  const lvl = COMPLEXITY_BY_KEY[t.complexity] || COMPLEXITY_BY_KEY.unclear;
  const shots = Number(item?.AttachmentCount) || 0;
  const lines = [
    `# Feedback #${item?.FeedbackID ?? "?"} — ${str(item?.Title, 200)}`,
    "",
    `- Type: ${str(item?.Type, 20) || "?"} · Priority: ${str(item?.Priority, 10) || "?"} · Submitted by: ${str(item?.SubmittedByName || item?.SubmittedByEmail, 200) || "?"}`,
    `- AI triage: ${lvl.label} (${Math.round(t.confidence * 100)}% confidence) · Area: ${t.area || "?"} · Category: ${t.category}`,
    shots ? `- ${shots} screenshot${shots === 1 ? "" : "s"} attached — view them in Help ▸ Feedback Inbox, item #${item?.FeedbackID}.` : "- No screenshots attached.",
    "",
    "## Submitted description",
    str(item?.Description, 4000) || "(none)",
    "",
    "## Triage summary",
    t.summary || "(none)",
    "",
    "## Proposed change",
    t.proposedChange || "(none)",
  ];
  if (t.likelyFiles.length) lines.push("", "## Likely files", ...t.likelyFiles.map(f => `- ${f}`));
  if (t.questions.length) lines.push("", "## Open questions (answer before building)", ...t.questions.map(q => `- ${q}`));
  if (t.riskNotes) lines.push("", "## Risk notes", t.riskNotes);
  if (t.duplicateHint) lines.push("", "## Possible duplicate", t.duplicateHint);
  lines.push(
    "",
    "## Instructions for the implementer",
    "- Read CLAUDE.md first; keep every security invariant (auth on endpoints, SQL only through the guard, no secrets).",
    "- One bounded PR against `main`, this item only. Do not widen scope.",
    "- Run `node scripts/security-guard.cjs`, `npm run lint`, `npm run check:static`, `npm run test` before opening the PR.",
    "- Open the PR for human review. Do not merge. Reference this feedback item in the PR body.",
    `- When it ships, set the item's status in Help ▸ Feedback Inbox to Resolved and note the PR.`,
  );
  return lines.join("\n");
}
